import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useCurrentUser } from '@/hooks/use-current-user'
import { queryClient } from '@/lib/query-client'
import { supabase } from '@/lib/supabase'
import type { ModuleKey } from '@/lib/modules'

// Mirrors the public.capabilities registry seeded by migration 033. The DB is
// the authority; this list exists so the client can name keys type-safely.
export const CAPABILITY_KEYS = [
  'accounts.view_all',
  'quotes.view_all',
  'pos.view_all',
  'records.write',
  'records.archive',
  'accounts.archive',
  'pos.import',
  'settings.manage',
  'users.manage',
  'roles.manage',
  'catalog.manage',
  'audit.view',
  'module.pipeline',
  'module.contracts',
  'module.products',
  'module.pos',
  'module.reports',
  'module.import',
] as const

export type CapabilityKey = (typeof CAPABILITY_KEYS)[number]

// The module registry (lib/modules) predates the capability registry and the
// two disagree on one name: the tenant module toggle is 'ams', its capability
// is 'module.contracts'. Same module, two spellings.
const MODULE_CAPABILITY: Record<ModuleKey, CapabilityKey> = {
  pipeline: 'module.pipeline',
  products: 'module.products',
  ams: 'module.contracts',
  import: 'module.import',
  pos: 'module.pos',
  reports: 'module.reports',
}

export interface Capabilities {
  has: (key: CapabilityKey) => boolean
  /** Per-module access, keyed by the tenant module registry's ModuleKey. */
  modules: Record<ModuleKey, boolean>

  canViewAllAccounts: boolean
  canViewAllQuotes: boolean
  canViewAllPos: boolean
  canWrite: boolean
  canArchive: boolean
  canArchiveAccounts: boolean
  canImportPos: boolean
  canManageSettings: boolean
  canManageUsers: boolean
  canManageRoles: boolean
  canManageCatalog: boolean
  canViewAudit: boolean

  /**
   * /admin route guard AND the admin nav entry — one flag, so navigation can
   * never advertise a section the guard bounces. True only for capabilities
   * with an actual admin page behind them: users.manage (Team Access) and
   * settings.manage (Modules). audit.view / roles.manage / catalog.manage
   * have no page in the section today; when one lands, its guard and this
   * flag move together. (R29: the old any-admin-capability aggregate walked
   * an audit.view-only user into six live module toggles the database
   * refuses.)
   */
  canAccessAdmin: boolean
  /** /settings route guard: branding and tenant settings. */
  canAccessSettings: boolean
  /** Fields marked adminOnly on EditableField (division, ownership, etc.). */
  canEditRestrictedFields: boolean
  canManageDivisions: boolean
}

/**
 * The one place capability keys become UI permissions. Pure, so route guards
 * (which run in beforeLoad, outside React) and the hook derive from the same
 * mapping. Unknown keys are ignored; absent keys deny.
 */
export function capabilitiesFromKeys(keys: readonly string[]): Capabilities {
  const granted = new Set<string>(keys)
  const has = (key: CapabilityKey) => granted.has(key)

  const canManageSettings = has('settings.manage')
  const canManageUsers = has('users.manage')
  const canManageRoles = has('roles.manage')
  const canManageCatalog = has('catalog.manage')
  const canViewAudit = has('audit.view')

  // Exactly the capabilities with an admin PAGE behind them — see the
  // interface doc. Not an any-admin-capability aggregate.
  const canAccessAdmin = canManageUsers || canManageSettings

  const modules = {} as Record<ModuleKey, boolean>
  for (const [module, capability] of Object.entries(MODULE_CAPABILITY) as [
    ModuleKey,
    CapabilityKey,
  ][]) {
    modules[module] = has(capability)
  }

  return {
    has,
    modules,

    canViewAllAccounts: has('accounts.view_all'),
    canViewAllQuotes: has('quotes.view_all'),
    canViewAllPos: has('pos.view_all'),
    canWrite: has('records.write'),
    canArchive: has('records.archive'),
    canArchiveAccounts: has('accounts.archive'),
    canImportPos: has('pos.import'),
    canManageSettings,
    canManageUsers,
    canManageRoles,
    canManageCatalog,
    canViewAudit,

    canAccessAdmin,
    canAccessSettings: canManageSettings,
    canEditRestrictedFields: canManageSettings,
    canManageDivisions: canManageSettings,
  }
}

/** Deny-all. The state before the RPC resolves, and for a signed-out user. */
export const NO_CAPABILITIES = capabilitiesFromKeys([])

const capabilityQuery = (userId: string | null) => ({
  queryKey: ['capabilities', userId] as const,
  queryFn: async (): Promise<string[]> => {
    const { data, error } = await supabase.rpc('current_capabilities')
    if (error) throw error
    return data ?? []
  },
  // Capabilities change only when an admin edits a role, but they must not
  // need a reload to take effect.
  staleTime: 60_000,
})

/**
 * For beforeLoad, which cannot use hooks. Shares the query cache with
 * useCapabilities, so a guard that already ran seeds the hook and vice versa.
 *
 * `fetchQuery`, NOT `ensureQueryData` (Codex F4): ensureQueryData returns
 * whatever the cache holds however stale it is, so a revoked admin kept
 * routing into privileged surfaces on the old grant until the entry happened
 * to refresh. fetchQuery serves the cache only within `staleTime` (no
 * per-navigation round-trip while fresh) and otherwise REVALIDATES and
 * awaits the fresh answer before any privileged routing decision. A failed
 * revalidation REJECTS — reaching the router error boundary — rather than
 * falling back to the cached grant: cached-but-unconfirmable is "unknown",
 * and unknown never opens a privileged surface.
 */
export async function fetchCapabilities(userId: string): Promise<Capabilities> {
  const keys = await queryClient.fetchQuery(capabilityQuery(userId))
  return capabilitiesFromKeys(keys)
}

export interface CapabilitiesWithStatus {
  caps: Capabilities
  /** True while the capability answer is not yet knowable (either read pending). */
  isLoading: boolean
  isError: boolean
  /** Retries the whole chain this status derives from (user + capabilities). */
  refetch: () => Promise<unknown>
}

/**
 * Pure status fold for the two-read chain behind capabilities, extracted so
 * the branching is unit-tested without renderHook. The chain is
 * current-user → capabilities RPC (the second query is disabled until the
 * first resolves, so its `isPending` is meaningless on its own — a disabled
 * TanStack query reports pending forever):
 *
 *  - current-user still pending → loading (capabilities are NOT yet known;
 *    reporting "denied" here painted a false "not available for your role");
 *  - current-user errored → error (unknown, not unauthorized);
 *  - resolved signed-out → neither loading nor error: denial is the KNOWN
 *    answer (inside `_authed` this never renders — beforeLoad redirects);
 *  - signed in → the capability query's own pending/error status.
 */
export function capabilitiesStatus(input: {
  userPending: boolean
  userError: boolean
  signedIn: boolean
  capsPending: boolean
  capsError: boolean
}): { isLoading: boolean; isError: boolean } {
  if (input.userPending) return { isLoading: true, isError: false }
  if (input.userError) return { isLoading: false, isError: true }
  if (!input.signedIn) return { isLoading: false, isError: false }
  return { isLoading: input.capsPending, isError: input.capsError }
}

/**
 * The one fold from a query snapshot to Capabilities, extracted pure so it is
 * unit-testable without renderHook (Codex F4). TanStack v5 KEEPS the last
 * successful `data` on a failed refetch, so `keys` being present says nothing
 * about whether the grants are still confirmable — the hook's own contract
 * ("in flight, errored, or signed out: deny") was silently false for exactly
 * that case, and privileged navigation kept rendering from the cached grant
 * after an errored refresh. An errored chain is deny-all, whatever the cache
 * still holds.
 */
export function resolveCapabilitySnapshot(
  keys: readonly string[] | undefined,
  isError: boolean,
): Capabilities {
  return keys && !isError ? capabilitiesFromKeys(keys) : NO_CAPABILITIES
}

/**
 * Capabilities plus the chain's status, for consumers that must distinguish
 * "denied" from "not yet known" (the module availability resolver renders
 * those differently). `caps` itself is identical to `useCapabilities()`:
 * deny-all until a successful read.
 */
export function useCapabilitiesWithStatus(): CapabilitiesWithStatus {
  const userQuery = useCurrentUser()
  const userId = userQuery.data?.id ?? null
  const query = useQuery({
    ...capabilityQuery(userId),
    enabled: userId !== null,
  })
  const keys = query.data
  const status = capabilitiesStatus({
    userPending: userQuery.isPending,
    userError: userQuery.isError,
    signedIn: userId !== null,
    capsPending: query.isPending,
    capsError: query.isError,
  })
  // In flight, errored, or signed out: deny. Never fall back to admin.
  const caps = useMemo(
    () => resolveCapabilitySnapshot(keys, status.isError),
    [keys, status.isError],
  )
  return {
    caps,
    ...status,
    // Refetch the whole chain: a capability error may really be a current-user
    // error. Note v5 DOES fetch a disabled query on explicit refetch(); while
    // the user is unresolved that stray result caches under
    // ['capabilities', null] and is abandoned when the key changes on user
    // resolve — harmless, and refetch() never rejects. The capability query
    // then auto-runs for the fresh key once the user resolves.
    refetch: () => Promise.all([userQuery.refetch(), query.refetch()]),
  }
}

export function useCapabilities(): Capabilities {
  return useCapabilitiesWithStatus().caps
}
